/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef } from 'react';
import type {
  ReactNode,
  Ref,
  ReactElement,
} from 'react';

import type {
  GroupBase,
} from 'react-select';
import type {
  UseAsyncPaginateParams,
  ComponentProps,
} from 'react-select-async-paginate';

import { SelectBase } from '../SelectBase';

import {
  texts,
} from '../ReactSelectExtended/texts';
import {
  useStyles,
} from '../ReactSelectExtended/styles';
import { selectComponentsAsync } from '../ReactSelectExtended/components';
import type {
  OptionTypeBase,
  SelectBaseProps,
} from '../ReactSelectExtended/types';

import {
  AsyncPaginateExtended,
} from '../ReactSelectExtended/ReactSelectExtended';
import type {
  ReactSelectExtendedProps,
} from '../ReactSelectExtended/ReactSelectExtended';

export type SelectAsyncProps<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
Additional,
IsMulti extends boolean,
> =
  & ReactSelectExtendedProps<OptionType, IsMulti, Group>
  & UseAsyncPaginateParams<OptionType, Group, Additional>
  & ComponentProps<OptionType, Group, IsMulti>
  & Omit<SelectBaseProps<OptionType, Group, IsMulti>, 'defaultComponents' | 'children'>;

function SelectAsyncComponent<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
Additional,
IsMulti extends boolean,
>(
  props: SelectAsyncProps<OptionType, Group, Additional, IsMulti>,
  ref: Ref<HTMLDivElement>,
): ReactElement {
  const {
    hasError = false,
    hasWarning = false,
    isClearable = true,
    isMulti = false,
    small = false,
    renderModal = undefined,
    selectRef = null,
    ...restProps
  } = props;

  const styles = useStyles(small);

  return (
    <SelectBase
      defaultComponents={selectComponentsAsync}
      {...props}
      ref={ref}
    >
      {({
        getOptionLabel,
        getOptionValue,
        components,
      }): ReactNode => (
        <AsyncPaginateExtended
          {...texts}
          isMulti={isMulti}
          hasError={hasError}
          hasWarning={hasWarning}
          small={small}
          closeMenuOnSelect={!isMulti}
          isArrowHidden={Boolean(renderModal)}
          components={components}
          styles={styles}
          {...restProps}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          isClearable={isClearable}
          selectRef={selectRef}
        />
      )}
    </SelectBase>
  );
}

export const SelectAsync = forwardRef(SelectAsyncComponent) as <
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
Additional,
IsMulti extends boolean,
>(
  props: SelectAsyncProps<OptionType, Group, Additional, IsMulti> & {
    ref?: Ref<HTMLDivElement>,
  },
) => ReactElement;
